import React, { useEffect, useMemo, useState } from "react";
import {
  Product,
  QuantityUnit,
  Recipe,
  RecipeIngredient,
  useAddSelectedRecipeIngredientsToShoppingListMutation,
} from "utils/grocyApi.ts";
import { toast } from "react-hot-toast";
import { useQueue } from "contexts/QueueContext.tsx";
import { useUnitConversion } from "hooks/useUnitConversion.ts";
import RecipeIngredientItem from "./RecipeIngredientItem.tsx";
import { RecipeFulfillmentStatus } from "./recipeFulfillment.ts";
import {
  buildRecipeConsumePreviewItems,
  getRecipeConsumePreviewText,
} from "./recipeConsumePreview.ts";
import { getRecipeShoppingListAmount } from "./recipeShoppingListAmount.ts";
import "./RecipeDetail.css";

const RECIPE_CONSUME_QUEUE_TOAST_ID = "recipe-consume-queue";

interface RecipeDetailProps {
  recipe: Recipe;
  recipeIngredients: RecipeIngredient[];
  products: Product[];
  quantityUnits: QuantityUnit[];
  recipeFulfillmentStatus?: RecipeFulfillmentStatus;
  onBack: () => void;
}

const RecipeDetail: React.FC<RecipeDetailProps> = ({
  recipe,
  recipeIngredients,
  products,
  quantityUnits,
  recipeFulfillmentStatus,
  onBack,
}) => {
  const { addConsumeOperation, queue } = useQueue();
  const addSelectedToShoppingListMutation =
    useAddSelectedRecipeIngredientsToShoppingListMutation();
  const conversionService = useUnitConversion();

  const ingredientItems = useMemo(
    () =>
      recipeIngredients
        .map((ingredient) => {
          const product = products.find((p) => p.id === ingredient.product_id);
          if (!product) return null;

          const recipeUnit = quantityUnits.find((u) =>
            u.id === ingredient.qu_id
          );
          const stockUnit = quantityUnits.find((u) =>
            u.id === product.qu_id_stock
          );
          const stockComparison = conversionService
            ?.getIngredientStockComparison(ingredient, product);
          const availableStockAmount = conversionService
            ?.getAggregatedStock(product.id) ?? 0;
          const fulfillmentStatus = recipeFulfillmentStatus?.ingredients.find(
            (item) => item.ingredientId === ingredient.id,
          );

          return {
            ingredient,
            product,
            recipeUnit,
            stockUnit,
            isMissing: stockComparison ? !stockComparison.isAvailable : false,
            requiredAmount: stockComparison?.convertedRequired ??
              ingredient.amount,
            availableStockAmount,
            isFullySatisfied: fulfillmentStatus
              ? fulfillmentStatus.shortfallAmount === 0 ||
                fulfillmentStatus.isCoveredByShoppingList
              : Boolean(stockComparison?.isAvailable),
            shortfallAmount: fulfillmentStatus?.shortfallAmount || 0,
            shoppingListCoverageAmount:
              fulfillmentStatus?.shoppingListCoverageAmount || 0,
            isCoveredByShoppingList:
              fulfillmentStatus?.isCoveredByShoppingList || false,
            isPartiallyCoveredByShoppingList:
              fulfillmentStatus?.isPartiallyCoveredByShoppingList || false,
          };
        })
        .filter((item): item is NonNullable<typeof item> => item !== null),
    [
      conversionService,
      products,
      quantityUnits,
      recipeFulfillmentStatus,
      recipeIngredients,
    ],
  );

  const selectionDefaults = useMemo(
    () =>
      Object.fromEntries(
        ingredientItems.map(
          (item) => [item.ingredient.id, !item.isFullySatisfied],
        ),
      ),
    [ingredientItems],
  );

  const selectionSeedKey = useMemo(
    () =>
      ingredientItems
        .map((item) => `${item.ingredient.id}:${item.isFullySatisfied ? 1 : 0}`)
        .join("|"),
    [ingredientItems],
  );

  const [selectedIngredients, setSelectedIngredients] = useState<
    Record<number, boolean>
  >({});
  const [isConsumePreviewActive, setIsConsumePreviewActive] = useState(false);
  const [consumePreviewSelection, setConsumePreviewSelection] = useState<
    Record<number, boolean>
  >({});
  const [queuedConsumeOperationIds, setQueuedConsumeOperationIds] = useState<
    string[]
  >([]);

  useEffect(() => {
    setSelectedIngredients(selectionDefaults);
    setIsConsumePreviewActive(false);
    setConsumePreviewSelection({});
  }, [recipe.id, selectionDefaults, selectionSeedKey]);

  const selectedIngredientItems = ingredientItems.filter((item) =>
    selectedIngredients[item.ingredient.id]
  );
  const canAddSelected = selectedIngredientItems.length > 0;
  const consumePreviewItems = useMemo(
    () =>
      buildRecipeConsumePreviewItems(
        ingredientItems.map((item) => ({
          ingredientId: item.ingredient.id,
          productId: item.product.id,
          requiredAmount: item.requiredAmount,
          availableStockAmount: item.availableStockAmount,
        })),
      ),
    [ingredientItems],
  );
  const consumePreviewItemsByIngredientId = useMemo(
    () =>
      new Map(
        consumePreviewItems.map((item) => [item.ingredientId, item]),
      ),
    [consumePreviewItems],
  );

  const enterConsumePreview = () => {
    setConsumePreviewSelection(
      Object.fromEntries(
        consumePreviewItems.map((item) => [
          item.ingredientId,
          item.defaultSelected,
        ]),
      ),
    );
    setIsConsumePreviewActive(true);
  };

  const confirmConsumePreview = () => {
    const operations = consumePreviewItems
      .filter((item) => consumePreviewSelection[item.ingredientId])
      .filter((item) => item.consumeAmount > 0);

    if (operations.length === 0) {
      toast("No selected ingredients have stock to consume.");
      return;
    }

    const operationIds = operations.map((item) =>
      addConsumeOperation(item.productId, item.consumeAmount)
    );

    setQueuedConsumeOperationIds((previous) => [
      ...previous,
      ...operationIds,
    ]);
    toast.loading(
      `Consuming added to queue. ${operations.length} operation${
        operations.length === 1 ? "" : "s"
      } left in queue.`,
      {
        id: RECIPE_CONSUME_QUEUE_TOAST_ID,
        duration: Infinity,
      },
    );
    setIsConsumePreviewActive(false);
    setConsumePreviewSelection({});
  };

  useEffect(() => {
    if (queuedConsumeOperationIds.length === 0) return;

    const queuedConsumeOperations = queuedConsumeOperationIds
      .map((operationId) =>
        queue.find((operation) => operation.id === operationId)
      )
      .filter((operation): operation is NonNullable<typeof operation> =>
        operation !== undefined
      );
    const activeCount = queuedConsumeOperations.filter((operation) =>
      operation.status === "PENDING" || operation.status === "IN_PROGRESS"
    ).length;
    const failedCount = queuedConsumeOperations.filter((operation) =>
      operation.status === "FAILED"
    ).length;

    if (activeCount > 0) {
      toast.loading(
        `Consuming added to queue. ${activeCount} operation${
          activeCount === 1 ? "" : "s"
        } left in queue.`,
        {
          id: RECIPE_CONSUME_QUEUE_TOAST_ID,
          duration: Infinity,
        },
      );
      return;
    }

    if (failedCount > 0) {
      const firstFailure = queuedConsumeOperations.find((operation) =>
        operation.status === "FAILED" && operation.errorMessage
      );
      toast.error(
        `${failedCount} consume operation${
          failedCount === 1 ? "" : "s"
        } failed.${
          firstFailure?.errorMessage ? ` ${firstFailure.errorMessage}` : ""
        }`,
        {
          id: RECIPE_CONSUME_QUEUE_TOAST_ID,
          duration: 10000,
        },
      );
    } else {
      toast.dismiss(RECIPE_CONSUME_QUEUE_TOAST_ID);
    }

    setQueuedConsumeOperationIds([]);
  }, [queue, queuedConsumeOperationIds]);

  return (
    <div className="recipe-detail">
      <header className="recipe-detail__header">
        <button
          type="button"
          className="recipe-detail__back-button"
          onClick={onBack}
        >
          ← Back
        </button>
        <h1 className="recipe-detail__title">{recipe.name}</h1>
        <div className="recipe-detail__servings">
          {recipe.desired_servings} servings
        </div>
      </header>

      <main className="recipe-detail__main">
        <section className="recipe-detail__ingredients">
          <h2 className="recipe-detail__section-title">Ingredients</h2>
          <div className="recipe-detail__ingredients-list">
            {ingredientItems.map((item, _index) => (
              <RecipeIngredientItem
                key={item.ingredient.id}
                ingredient={item.ingredient}
                product={item.product}
                recipeUnit={item.recipeUnit}
                stockUnit={item.stockUnit}
                shortfallAmount={item.shortfallAmount}
                shoppingListCoverageAmount={item.shoppingListCoverageAmount}
                isCoveredByShoppingList={item.isCoveredByShoppingList}
                isPartiallyCoveredByShoppingList={item
                  .isPartiallyCoveredByShoppingList}
                checked={isConsumePreviewActive
                  ? Boolean(consumePreviewSelection[item.ingredient.id])
                  : Boolean(selectedIngredients[item.ingredient.id])}
                selectorLabel={isConsumePreviewActive
                  ? `Select ${item.product.name} for consumption`
                  : undefined}
                previewText={isConsumePreviewActive
                  ? getRecipeConsumePreviewText(
                    consumePreviewItemsByIngredientId.get(
                      item.ingredient.id,
                    )!,
                    Boolean(consumePreviewSelection[item.ingredient.id]),
                  )
                  : undefined}
                onToggle={() => {
                  if (isConsumePreviewActive) {
                    setConsumePreviewSelection((previous) => ({
                      ...previous,
                      [item.ingredient.id]: !previous[item.ingredient.id],
                    }));
                  } else {
                    setSelectedIngredients((previous) => ({
                      ...previous,
                      [item.ingredient.id]: !previous[item.ingredient.id],
                    }));
                  }
                }}
              />
            ))}
          </div>
        </section>

        <section className="recipe-detail__actions">
          <div className="recipe-detail__action-buttons">
            {!isConsumePreviewActive && (
              <button
                type="button"
                className="recipe-detail__action-button recipe-detail__action-button--add-to-list"
                onClick={() =>
                  addSelectedToShoppingListMutation.mutate({
                    recipeId: recipe.id,
                    recipeName: recipe.name,
                    items: selectedIngredientItems.map((item) => ({
                      product_id: item.product.id,
                      product_amount: item.isFullySatisfied
                        ? getRecipeShoppingListAmount(
                          item.ingredient,
                          item.product,
                          item.stockUnit,
                          conversionService,
                        )
                        : Math.max(
                          getRecipeShoppingListAmount(
                            item.ingredient,
                            item.product,
                            item.stockUnit,
                            conversionService,
                          ) - item.shoppingListCoverageAmount,
                          0,
                        ),
                      qu_id: item.product.qu_id_stock,
                    }))
                      .filter((item) => item.product_amount > 0),
                  })}
                disabled={!canAddSelected ||
                  addSelectedToShoppingListMutation.isPending}
              >
                {addSelectedToShoppingListMutation.isPending
                  ? "Adding..."
                  : "Add Selected to shopping list"}
              </button>
            )}
            {isConsumePreviewActive && (
              <button
                type="button"
                className="recipe-detail__action-button recipe-detail__action-button--cancel"
                onClick={() => {
                  setIsConsumePreviewActive(false);
                  setConsumePreviewSelection({});
                }}
              >
                Cancel
              </button>
            )}
            <button
              type="button"
              className="recipe-detail__action-button recipe-detail__action-button--consume"
              onClick={() => {
                if (isConsumePreviewActive) {
                  confirmConsumePreview();
                } else {
                  enterConsumePreview();
                }
              }}
            >
              {isConsumePreviewActive
                ? "Consume Selected Ingredients"
                : "Preview Ingredients to Consume"}
            </button>
          </div>
        </section>
      </main>
    </div>
  );
};

export default RecipeDetail;
